import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge as RawBadge } from "@/components/ui/badge";
import { Ship, Car, Bed, Flag, Clock, CreditCard, Users, Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { getGolferRoster } from "@/utils/golferRoster";
import {
  calculateBookingTotal,
  deriveTeePeriod,
  toDateKey,
} from "@/utils/pricingEngine";

function toJsDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate(); // Firestore Timestamp
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function formatDate(value) {
  const d = toJsDate(value);
  if (!d) return null;
  return format(d, "dd MMM yyyy");
}

function wib(t) {
  return t ? `${t} WIB` : "—";
}

const statusColors = {
  confirmed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-800",
  pending: "bg-yellow-100 text-yellow-800",
};

const paymentStatusColors = {
  paid: "bg-green-100 text-green-800",
  unpaid: "bg-amber-100 text-amber-800",
  refunded: "bg-gray-100 text-gray-700",
};

/**
 * @param {{
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 *   booking: Record<string, any> | null,
 * }} props
 */
export default function BookingDetailsDialog({ open, onOpenChange, booking }) {
  const Badge = /** @type {React.ComponentType<any>} */ (RawBadge);
  const AnyDialogContent = /** @type {any} */ (DialogContent);
  if (!booking) return null;

  const nights = booking.selectedPackage?.nights ?? 0;
  const rounds = booking.selectedPackage?.rounds ?? booking.selectedCourses?.length ?? 0;
  const players = booking.numPlayers || 1;
  const roster = getGolferRoster(booking);
  const rooms = booking.numRooms || players;

  // Bookings without a stored priceBreakdown are re-priced with the same
  // engine checkout uses — course/ferry `price` fields are per-player rates,
  // so summing them directly under-counts.
  const calculated = (() => {
    const rounds = (booking.selectedCourses || []).map((course, i) => ({
      course,
      date: toJsDate(booking.roundDates?.[i]),
      period: deriveTeePeriod(booking.roundTimes?.[i]),
    }));
    const checkIn = toJsDate(booking.checkInDate);
    const nightDates = checkIn
      ? Array.from({ length: nights }, (_, i) => {
          const d = new Date(checkIn);
          d.setDate(d.getDate() + i);
          return d;
        })
      : [];
    const roundPeriodByDate = Object.fromEntries(
      rounds
        .filter((r) => r.date)
        .map((r) => [toDateKey(r.date), r.period || "AM"]),
    );
    try {
      return calculateBookingTotal({
        vehicle: booking.selectedTransport,
        vehicleCourse: rounds[0]?.course,
        ferry: booking.selectedFerry,
        accommodation: booking.selectedAccommodation,
        totalGuests: players,
        rounds,
        nightDates,
        sharingType: booking.sharingType,
        numRooms: rooms,
        roundPeriodByDate,
      });
    } catch {
      return {};
    }
  })();
  const calculatedGolfTotal = calculated.golfTotal || 0;
  const calculatedAccommodationTotal = calculated.accommodationTotal || 0;
  const calculatedFerryTotal = calculated.ferryTotal || 0;
  const calculatedTransportTotal = calculated.vehicleTotal || 0;
  const priceBreakdown = booking.priceBreakdown || {};
  const golfTotal = Number(priceBreakdown.golfTotal ?? calculatedGolfTotal);
  const accommodationTotal = Number(
    priceBreakdown.accommodationTotal ?? calculatedAccommodationTotal,
  );
  const ferryTotal = Number(priceBreakdown.ferryTotal ?? calculatedFerryTotal);
  const transportTotal = Number(
    priceBreakdown.transportTotal ?? calculatedTransportTotal,
  );
  const discountAmount = Number(booking.discountAmount) || 0;
  const grandTotal =
    booking.grandTotal ??
    golfTotal + accommodationTotal + ferryTotal + transportTotal - discountAmount;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <AnyDialogContent className="sm:max-w-xl max-h-[85vh] overflow-y-auto min-w-0">
        <DialogHeader className="min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-3 pr-8">
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-[#D4AF37] uppercase tracking-widest mb-0.5 truncate">
                {booking.reservationNumber}
              </p>
              <DialogTitle className="text-[#0B2421] text-xl truncate">
                {rounds} Round{rounds !== 1 ? "s" : ""} / {nights} Night{nights !== 1 ? "s" : ""}
              </DialogTitle>
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              {booking.status && (
                <Badge className={statusColors[booking.status === "pending_payment" ? "pending" : booking.status] || "bg-gray-100 text-gray-700"}>
                    {booking.status === "pending_payment" ? "pending" : booking.status}
                </Badge>
              )}
              {booking.paymentStatus && (
                <Badge className={paymentStatusColors[booking.paymentStatus] || "bg-gray-100 text-gray-700"}>
                  {booking.paymentStatus}
                </Badge>
              )}
            </div>
          </div>
          <DialogDescription>Full booking details</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2 min-w-0">
          {/* Trip dates + players */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 bg-gray-50 rounded-xl px-4 py-3">
            {booking.startDate && booking.endDate && (
              <span className="flex items-center gap-1.5">
                <CalendarIcon className="h-4 w-4 text-[#D4AF37]" />
                {formatDate(booking.startDate)} – {formatDate(booking.endDate)}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Users className="h-4 w-4 text-[#D4AF37]" />
              {players} player{players !== 1 ? "s" : ""}
            </span>
          </div>

          {/* Golfer Details — lead golfer (the account holder) always first */}
          {roster.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-[#D4AF37]" />
                Golfer Details
              </p>
              <div className="flex flex-wrap gap-2">
                {roster.map((g, i) => (
                  <span
                    key={i}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border ${
                      g.isLead
                        ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                        : "bg-gray-50 border-gray-200 text-gray-700"
                    }`}
                  >
                    {g.name}
                    {g.isLead && (
                      <span className="text-[9px] font-bold uppercase tracking-wide">
                        · Lead
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Golf rounds */}
          {booking.selectedCourses?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                <Flag className="h-3.5 w-3.5 text-[#D4AF37]" />
                Golf Rounds
              </p>
              <div className="space-y-2">
                {booking.selectedCourses.map((course, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-xl border border-gray-100 p-3 min-w-0">
                    {course?.image && (
                      <img src={course.image} alt={course.name} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-[#0B2421] text-sm truncate">
                        Round {i + 1}: {course?.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {[course?.city, course?.country].filter(Boolean).join(", ")}
                      </p>
                      {booking.roundDates?.[i] && (
                        <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
                          <Clock className="h-3 w-3 shrink-0" />
                          {formatDate(booking.roundDates[i])}
                          {booking.roundTimes?.[i] ? ` · ${wib(booking.roundTimes[i])}` : ""}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2 text-sm">
                  <span className="font-semibold text-[#0B2421]">Total Golf</span>
                  <span className="font-bold text-[#0B2421]">SGD {golfTotal}</span>
                </div>
              </div>
            </div>
          )}

          {/* Ferry */}
          {booking.selectedFerry && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                <Ship className="h-3.5 w-3.5 text-[#D4AF37]" />
                Ferry (Round Trip)
              </p>
              <div className="flex items-center gap-3 rounded-xl border border-gray-100 p-3 min-w-0">
                {booking.selectedFerry.image && (
                  <img src={booking.selectedFerry.image} alt={booking.selectedFerry.title} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-[#0B2421] text-sm truncate">{booking.selectedFerry.title}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {booking.selectedFerry.originport} ⇄ {booking.selectedFerry.destination}
                  </p>
                  {booking.selectedFerryTime && (
                    <p className="text-xs text-gray-400 mt-0.5">Inbound departs {wib(booking.selectedFerryTime)}</p>
                  )}
                  {booking.selectedOutboundFerryTime && (
                    <p className="text-xs text-gray-400 mt-0.5">Outbound departs {wib(booking.selectedOutboundFerryTime)}</p>
                  )}
                </div>
                <p className="text-sm font-bold text-[#0B2421] shrink-0">SGD {ferryTotal}</p>
              </div>
            </div>
          )}

          {/* Land transport */}
          {booking.selectedTransport && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                <Car className="h-3.5 w-3.5 text-[#D4AF37]" />
                Land Transport
              </p>
              <div className="flex items-center gap-3 rounded-xl border border-gray-100 p-3 min-w-0">
                {booking.selectedTransport.image && (
                  <img src={booking.selectedTransport.image} alt={booking.selectedTransport.title} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-[#0B2421] text-sm truncate">
                    {booking.selectedTransport.title}
                    {booking.selectedTransport.type ? ` · ${booking.selectedTransport.type}` : ""}
                  </p>
                  {booking.selectedTransportTime && (
                    <p className="text-xs text-gray-400 mt-0.5">Inbound pickup {wib(booking.selectedTransportTime)}</p>
                  )}
                  {booking.selectedOutboundTransportTime && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Return pickup {wib(booking.selectedOutboundTransportTime)}
                      {booking.selectedOutboundTransport ? ` · ${booking.selectedOutboundTransport.title}` : ""}
                    </p>
                  )}
                </div>
                <p className="text-sm font-bold text-[#0B2421] shrink-0">SGD {transportTotal}</p>
              </div>
            </div>
          )}

          {/* Accommodation */}
          {booking.selectedAccommodation && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                <Bed className="h-3.5 w-3.5 text-[#D4AF37]" />
                Accommodation
              </p>
              <div className="flex items-center gap-3 rounded-xl border border-gray-100 p-3 min-w-0">
                {booking.selectedAccommodation.image && (
                  <img src={booking.selectedAccommodation.image} alt={booking.selectedAccommodation.title} className="w-12 h-12 rounded-lg object-cover shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-[#0B2421] text-sm truncate">{booking.selectedAccommodation.title}</p>
                  <p className="text-xs text-gray-500">
                    {booking.selectedAccommodation.destination} · {nights} night{nights !== 1 ? "s" : ""} · {rooms} room{rooms !== 1 ? "s" : ""}
                  </p>
                  {booking.checkInDate && booking.checkOutDate && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      {formatDate(booking.checkInDate)} – {formatDate(booking.checkOutDate)}
                    </p>
                  )}
                  {booking.checkInTime && (
                    <p className="text-xs text-gray-400 mt-0.5">Check-in {wib(booking.checkInTime)} (fixed)</p>
                  )}
                </div>
                <p className="text-sm font-bold text-[#0B2421] shrink-0">SGD {accommodationTotal}</p>
              </div>
            </div>
          )}

          {/* Price breakdown */}
          <div className="rounded-xl bg-gray-50 p-4 space-y-1.5 min-w-0">
            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">Price Breakdown</p>
            <div className="flex justify-between gap-3 text-sm text-gray-600">
              <span className="min-w-0">Golf ({booking.selectedCourses?.length || 0} round{(booking.selectedCourses?.length || 0) !== 1 ? "s" : ""})</span>
              <span className="shrink-0">SGD {golfTotal}</span>
            </div>
            {booking.selectedFerry && (
              <div className="flex justify-between gap-3 text-sm text-gray-600">
                <span className="min-w-0">Ferry (round trip, {players} passenger{players !== 1 ? "s" : ""})</span>
                <span className="shrink-0">SGD {ferryTotal}</span>
              </div>
            )}
            {booking.selectedTransport && (
              <div className="flex justify-between gap-3 text-sm text-gray-600">
                <span className="min-w-0">Land Transport</span>
                <span className="shrink-0">SGD {transportTotal}</span>
              </div>
            )}
            {booking.selectedAccommodation && (
              <div className="flex justify-between gap-3 text-sm text-gray-600">
                <span className="min-w-0">Accommodation ({nights} night{nights !== 1 ? "s" : ""})</span>
                <span className="shrink-0">SGD {accommodationTotal}</span>
              </div>
            )}
            {discountAmount > 0 && (
              <div className="flex justify-between gap-3 text-sm text-green-700">
                <span className="min-w-0">
                  Discount{booking.appliedCoupon?.code ? ` (${booking.appliedCoupon.code})` : ""}
                </span>
                <span className="shrink-0">- SGD {discountAmount}</span>
              </div>
            )}
            <div className="border-t border-gray-200 pt-2 mt-1 flex justify-between gap-3 font-bold text-[#0B2421]">
              <span className="min-w-0">Total</span>
              <span className="shrink-0">SGD {grandTotal}</span>
            </div>
          </div>

          {/* Payment */}
          <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 px-4 py-3 min-w-0">
            <span className="text-sm text-gray-500 flex items-center gap-1.5 min-w-0">
              <CreditCard className="h-4 w-4 shrink-0" />
              Payment Method
            </span>
            <span className="text-sm font-semibold text-[#0B2421] shrink-0">{booking.paymentMethod || "—"}</span>
          </div>
        </div>
      </AnyDialogContent>
    </Dialog>
  );
}